// Drift: Brugere og roller og invitationen på Supabase (del af SupabaseApi – se supabase.ts).
// Reglerne ligger i 20261029200100_brugere.sql (evnen brugere.ret og rollereglerne fra core/roller.ts). Login-brugeren hos
// Auth – invitationsmail, link til ny adgangskode og adgangskode sat af kontoret – laves af edge-funktionen "brugere".
// Linkene sendes kun i mailen, så { link } er tom i drift. Invitationen åbnes fra mailen (…/bekraeft?…&inv=1 → #/invitation/inv):
// linket logger personen ind, og hun opretter adgangskoden og bliver aktiv (accepter_invitation).
import { ApiFejl, type Api, type Bruger, type BrugerVisning } from './api';
import type { Rolle } from '../../core/typer';
import { adgangskodeFejl, email as tjekEmail, rens, telefon as tjekTelefon } from '../../core/validering';
import { gyldigtPostnr } from '../../core/geo';
import { ID_TYPER, tjekFil, validerKoerekort, validerLoen } from '../../core/medarbejderdata';
import { iDag } from '../../core/dato';
import type { SbKontekst } from './sbKatalog';
import type { SbMedarbejder } from './sbMedarbejder';

const fejl = (b: string, f: Record<string, string> = {}) => new ApiFejl(b, f);
const tjek = (f: Record<string, string>) => { if (Object.keys(f).length) throw fejl('Tjek de markerede felter.', f); };
type Invitation = { klient: string; navn: string; email: string; rolle: Exclude<Rolle, 'kunde'> };

export class SbBrugere {
  constructor(private readonly c: SbKontekst, private readonly medarbejder: SbMedarbejder, private readonly opdater: () => Promise<Bruger | null>) {}
  private async rpc<T>(navn: string, args: Record<string, unknown> = {}): Promise<T> {
    return (await this.c.k(this.c.sb.rpc(navn, { k: await this.c.kraevKlient(), ...args }))) as T;
  }
  /** Edge-funktionen brugere. spaerret: udsendelsen er spærret – ingen mail er sendt. */
  private async auth(profil: string, handling: 'inviter' | 'nulstil' | 'kode', ekstra: Record<string, unknown> = {}): Promise<{ link: string; spaerret: boolean }> {
    const r = await this.c.funktion<{ spaerret?: boolean } | null>('brugere', { klient: await this.c.kraevKlient(), profil, handling, ...ekstra });
    this.c.skift();
    return { link: '', spaerret: !!r?.spaerret };
  }

  brugere: Api['brugere'] = async () => ((await this.rpc<BrugerVisning[] | null>('brugere_liste')) ?? []);
  private static tjekInvitation(v: Parameters<Api['inviter']>[0]) {
    const f: Record<string, string> = {};
    if (rens(v.navn, 80).length < 2) f.navn = 'Skriv navnet.';
    if (!tjekEmail(v.email)) f.email = 'Skriv en gyldig e-mail.';
    if (v.telefon && !tjekTelefon(v.telefon)) f.telefon = 'Skriv et mobilnummer med 8 cifre.';
    if (v.rolle === 'steamer' && !gyldigtPostnr(v.steamer?.start_postnr ?? '')) f.start_postnr = 'Skriv postnummeret, hvor steameren starter dagen.';
    tjek(f);
  }
  inviter: Api['inviter'] = async (v) => {
    SbBrugere.tjekInvitation(v);
    const id = await this.rpc<string>('inviter_bruger', { p: v });
    this.c.skift();
    return this.auth(id, 'inviter');
  };
  /** Rollen låses til steamer – også i databasen (opret_steamer_fra_ansoegning). Mailen sendes som enhver anden invitation. */
  opretSteamerFraAnsoegning: Api['opretSteamerFraAnsoegning'] = async (ansoegning, v) => {
    SbBrugere.tjekInvitation({ ...v, rolle: 'steamer' });
    const id = await this.rpc<string>('opret_steamer_fra_ansoegning', { p_ansoegning: ansoegning, p: v });
    this.c.skift();
    return this.auth(id, 'inviter');
  };
  forlaengInvitation: Api['forlaengInvitation'] = async (id) => { const r = await this.rpc<string>('forlaeng_invitation', { p_id: id }); this.c.skift(); return r; };
  retBruger: Api['retBruger'] = async (id, r) => {
    if (r.navn != null && rens(r.navn, 80).length < 2) throw fejl('Skriv navnet.', { navn: 'Skriv navnet.' });
    if (r.telefon && !tjekTelefon(r.telefon)) throw fejl('Skriv et mobilnummer med 8 cifre.', { telefon: 'Ugyldigt nummer.' });
    await this.rpc('ret_bruger', { p_id: id, r });
    this.c.skift();
  };
  saetStatus: Api['saetStatus'] = async (id, status) => { await this.rpc('saet_bruger_status', { p_id: id, p_status: status }); this.c.skift(); };
  sendInvitationIgen: Api['sendInvitationIgen'] = (id) => this.auth(id, 'inviter');
  sletInvitation: Api['sletInvitation'] = async (id) => { await this.rpc('slet_invitation', { p_id: id }); this.c.skift(); };
  sendNulstilLink: Api['sendNulstilLink'] = (id) => this.auth(id, 'nulstil');
  saetKode: Api['saetKode'] = async (id, kode) => {
    const f = adgangskodeFejl(kode); if (f) throw fejl(f, { kode: f });
    await this.auth(id, 'kode', { kode });
  };
  overdragEjerskab: Api['overdragEjerskab'] = async (id, bekraeft) => {
    if (bekraeft.trim().toUpperCase() !== 'OVERDRAG') throw fejl('Skriv OVERDRAG for at bekræfte.', { bekraeft: 'Skriv OVERDRAG.' });
    await this.rpc('overdrag_ejerskab', { p_id: id, p_bekraeft: bekraeft });
    await this.opdater();
    this.c.skift();
  };

  // ---------- Den inviterede (logget ind med linket fra mailen) ----------
  private async invitation(): Promise<Invitation | null> {
    const a = this.c.adresse();
    if (!a || !(await this.c.sb.auth.getSession()).data.session) return null;
    return (await this.c.k(this.c.sb.rpc('min_invitation', { vaert: a.vaert, sti: a.slug }))) as Invitation | null;
  }
  /** Til tjekToken('inv'): gyldig, når personen er logget ind med linket og har en åben invitation på denne adresse. */
  async tjekInvitation() {
    const i = await this.invitation();
    return i ? { gyldig: true, type: 'invitation' as const, navn: i.navn, email: i.email, rolle: i.rolle as Rolle, ny: true }
      : { gyldig: false, aarsag: 'Invitationen er udløbet, allerede brugt eller trukket tilbage. Bed din administrator om en ny.' };
  }
  accepterInvitation: Api['accepterInvitation'] = async (_token, v) => {
    const i = await this.invitation();
    if (!i) throw fejl('Invitationen er udløbet, allerede brugt eller trukket tilbage. Bed din administrator om en ny.');
    const f: Record<string, string> = {};
    if (rens(v.navn, 80).length < 2) f.navn = 'Skriv dit navn.';
    if (!tjekTelefon(v.telefon)) f.telefon = 'Skriv et mobilnummer med 8 cifre.';
    const kf = adgangskodeFejl(v.kode, i.email); if (kf) f.kode = kf;
    const steamer = i.rolle === 'steamer';
    const loen = steamer ? validerLoen(v.loen ?? {}) : null;
    if (loen && !loen.ok) Object.assign(f, loen.fejl);
    const d = v.dokument;
    if (steamer) {
      if (!d) f.dokument = 'Upload et billede af dit sundhedskort, kørekort eller pas.';
      else if (!ID_TYPER.some(([id]) => id === d.type)) f.dokument = 'Vælg hvilket ID du uploader.';
      else { const tf = tjekFil(d.data); if ('fejl' in tf) f.dokument = tf.fejl; }
      Object.assign(f, validerKoerekort(d, v.koerekort, iDag()));
    }
    tjek(f);
    await this.c.k(this.c.sb.auth.updateUser({ password: v.kode }));
    const a = this.c.adresse()!;
    // Udløbsdatoen tjekkes også i databasen (kørekort er et krav for alle steamere)
    await this.c.k(this.c.sb.rpc('accepter_invitation', { vaert: a.vaert, sti: a.slug, p_navn: v.navn, p_telefon: v.telefon, ...(steamer ? { p_koerekort: v.koerekort?.gyldig_til } : {}) }));
    const b = await this.opdater();
    if (!b) throw fejl('Din adgang er oprettet. Log ind med din e-mail og adgangskode.');
    // Steameren: oplysninger til løn, ID'et og kørekortets sider gemmes med det samme (krypteret / privat bucket)
    if (loen?.ok) await this.medarbejder.gemMineLoenoplysninger(loen.data);
    if (steamer && d) {
      await this.medarbejder.uploadMitDokument(d);
      if (d.type !== 'koerekort' && v.koerekort?.forside) await this.medarbejder.uploadMitDokument({ ...v.koerekort.forside, type: 'koerekort' });
      if (v.koerekort?.bagside) await this.medarbejder.uploadMitDokument({ ...v.koerekort.bagside, type: 'koerekort_bag' });
    }
    return b;
  };
}
